import { format, parseISO } from "date-fns";
import { type TFunction } from "i18next";

import { formatNumber } from "../../../utils";
import { ActivityActor, ActivityEvent, ActivityReason } from "./types";

export const STOCK_HISTORY_EVENT_TYPES = [
  "STOCK_EDITED",
  "STOCK_REMOVED",
  "STOCK_LISTED_OUTSIDE_FARM2GO",
  "STOCK_RELISTED",
  "STOCK_UPDATED",
];

export const formatDateTime = (value: string | null | undefined): string =>
  value ? format(parseISO(value), "d MMM yyyy, HH:mm") : "–";

export const formatDate = (value: string | null | undefined): string =>
  value ? format(parseISO(value), "d MMM yyyy") : "–";

export const eventTime = (event: ActivityEvent): string | null =>
  event.occurred_at ?? event.recorded_at;

const timestampOf = (value: string | null | undefined): number =>
  value ? parseISO(value).getTime() : Number.NEGATIVE_INFINITY;

export const newestFirst = <T>(items: T[], timeOf: (item: T) => string | null | undefined): T[] =>
  [...items]
    .reverse()
    .sort((first, second) => timestampOf(timeOf(second)) - timestampOf(timeOf(first)));

export const reasonText = (reason: ActivityReason | null | undefined, language: string) =>
  reason ? (reason.translations?.[language] ?? reason.text) : null;

export const activityTypeLabelKey = (event: ActivityEvent): string =>
  event.type === "OFFER_REJECTED" && event.actor
    ? `farmersMarket.activity.types.OFFER_REJECTED_BY_${event.actor.role}`
    : `farmersMarket.activity.types.${event.type}`;

const roleText = (role: unknown, t: TFunction): string | null =>
  typeof role === "string" ? t(`farmersMarket.activity.roles.${role}`) : null;

const recordedByLine = (recordedBy: unknown, t: TFunction): string | null => {
  const role = roleText(recordedBy, t);
  return role ? t("farmersMarket.activity.delivery.recordedBy", { role }) : null;
};

export const activityDetailLines = (event: ActivityEvent, t: TFunction): string[] => {
  const details = event.details ?? {};
  const lines: (string | null)[] = [];
  switch (event.type) {
    case "DELIVERY_RECORDED": {
      const method = details.payment_method as { name?: string | null } | null | undefined;
      lines.push(
        recordedByLine(details.recorded_by, t),
        details.delivered_quantity
          ? t("farmersMarket.activity.delivery.quantity", {
              quantity: formatNumber(Number(details.delivered_quantity)),
            })
          : null,
        t("farmersMarket.activity.delivery.payment", {
          amount: details.payment_amount
            ? formatNumber(Number(details.payment_amount))
            : t("farmersMarket.activity.delivery.notPaid"),
        }),
        method?.name ? t("farmersMarket.activity.delivery.method", { method: method.name }) : null
      );
      break;
    }
    case "DELIVERY_RECORD_CONFIRMED":
      lines.push(recordedByLine(details.recorded_by, t));
      break;
    case "DELIVERY_RECORD_DISPUTED":
      lines.push(
        Array.isArray(details.topics) && details.topics.length
          ? t("farmersMarket.activity.delivery.topics", {
              topics: details.topics.map(topic => t(`deliveryTopics.${String(topic)}`)).join(", "),
            })
          : null
      );
      break;
    case "STOCK_UNASSIGNED_FROM_OFFER":
      lines.push(
        typeof details.cause === "string"
          ? t(`farmersMarket.activity.causes.${details.cause}`)
          : null
      );
      break;
    default:
      break;
  }
  return lines.filter((line): line is string => !!line);
};

export const actorLabel = (actor: ActivityActor | null, t: TFunction): string => {
  if (!actor) {
    return t("farmersMarket.activity.roles.SYSTEM");
  }
  if (actor.role === "BUYER" && !actor.organization && !actor.user) {
    return t("farmersMarket.activity.anonymousBuyer");
  }
  return [
    t(`farmersMarket.activity.roles.${actor.role}`),
    actor.organization?.name,
    actor.user?.name,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const depositPagePath = (readOnly: boolean, uuid: string): string =>
  `${readOnly ? "/wfpManager" : "/representative"}/market/deposit/${uuid}`;
